import {
  AppShell,
  type NavItem,
  type SearchCommand,
  type SwitcherApp,
  type UseNavigationBlock,
} from "@nymbl/nos2-rules";
import { useBlocker, useRouter, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";
import { ShellLink } from "@/components/layout/ShellLink";
import { useCurrentPerson } from "@/hooks/use-current-person";
import { useExpensePending } from "@/hooks/use-expenses";
import { useAvatarUrl } from "@/hooks/use-files";
import { useMyApps, useToggleMenuApp } from "@/hooks/use-my-apps";
import { useMyProfile } from "@/hooks/use-my-profile";
import { usePendingApprovals } from "@/hooks/use-team";
import { usePrefetchAssignable } from "@/hooks/use-time-assignable";
import { signOut, useSession } from "@/lib/auth-client";
import { avatarImage } from "@/lib/person-photo";
import { queryClient } from "@/lib/query";
import { ptoNavCount, teamNavCount } from "@/lib/team/pending";
import { navItems, visibleNavItems } from "./nav-items";

const useNavigationBlock: UseNavigationBlock = (shouldBlock) =>
  useBlocker({ shouldBlockFn: shouldBlock, enableBeforeUnload: false });

const SETTINGS_HREF = "/dashboard/settings";

interface SignedInUser {
  name: string;
  email: string;
  role?: string | null;
  image?: string | null;
}

interface DashboardLayoutProps {
  /** The session the route guard read, until the live one arrives. */
  user: SignedInUser;
  children: React.ReactNode;
}

/** The shell, fed with this person's menu, counts and apps. */
export function DashboardLayout({ user: guarded, children }: DashboardLayoutProps) {
  usePrefetchAssignable();
  const { data: session } = useSession();
  const router = useRouter();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const me = useCurrentPerson().data;
  const photoUrl = useMyProfile().data?.photoUrl;
  // One count read feeds both pills (My Team: the team count; PTO: the HR count).
  const pending = usePendingApprovals().data;
  const expenseCount = useExpensePending(me?.expensesEnabled ?? false).data?.count ?? 0;
  const myApps = useMyApps().data;
  const toggleApp = useToggleMenuApp();

  const user = session?.user ?? guarded;
  const image = useAvatarUrl(avatarImage(user.image, photoUrl));

  const nav = useMemo<NavItem[]>(
    () =>
      visibleNavItems(navItems, me?.managesAnyone ?? false, me?.expensesEnabled).map(
        ({ label, icon, href, badge }) => {
          // My Team: the manager's own count; PTO: HR-routed items waiting on the HR approver.
          const count =
            badge === "ptoHr"
              ? ptoNavCount(pending)
              : badge === "expenses"
                ? expenseCount
                : href === "/dashboard/team"
                  ? teamNavCount(pending)
                  : 0;
          return { label, icon, href, count, countLabel: "to approve" };
        },
      ),
    [me?.managesAnyone, me?.expensesEnabled, pending, expenseCount],
  );

  const isAdmin = user.role === "admin";
  const goTo = useMemo<SearchCommand[]>(
    () => [
      ...nav.map(({ label, href, icon }) => ({ id: href, label, href, icon })),
      { id: "/dashboard/profile", label: "Profile", href: "/dashboard/profile" },
      ...(isAdmin ? [{ id: SETTINGS_HREF, label: "Settings", href: SETTINGS_HREF }] : []),
    ],
    [nav, isAdmin],
  );

  const apps: SwitcherApp[] = myApps?.apps ?? [];
  const menu = myApps?.menu ?? [];

  function setAppShown(app: SwitcherApp, show: boolean) {
    const shown = new Set(menu);
    const next = apps.filter((a) => (a.id === app.id ? show : shown.has(a.id))).map((a) => a.id);
    toggleApp.mutate({ app, show, apps: next });
  }

  async function handleSignOut() {
    await signOut();
    queryClient.clear();
    await router.navigate({ to: "/login" });
  }

  return (
    <AppShell
      app="employee-dashboard"
      Link={ShellLink}
      currentPath={currentPath}
      onNavigate={(href) => void router.navigate({ to: href })}
      useNavigationBlock={useNavigationBlock}
      nav={nav}
      user={{ name: me?.name ?? user.name, email: user.email, image }}
      profileHref="/dashboard/profile"
      settingsHref={isAdmin ? SETTINGS_HREF : undefined}
      onSignOut={handleSignOut}
      apps={apps}
      navApps={apps.filter((a) => menu.includes(a.id))}
      onNavAppChange={setAppShown}
      homeUrl={window.location.origin}
      search={async () => []}
      searchOptions={{ goTo }}
    >
      {children}
    </AppShell>
  );
}
